import React, { FC, useContext } from 'react'
import classnames from 'classnames'
import { includes } from 'lodash'
import { useTranslations } from 'next-intl'
import {
  PASSWORD_ERROR_NECESSARY_USE,
  PASSWORD_ERROR_MIN_AND_MAX_LENGHT,
  PASSWORD_ERROR_WITHOUT_SPACES,
  PASSWORD_ERROR_WITH_UPPER_CASE,
  PASSWORD_ERROR_WITH_LOWER_CASE,
  PASSWORD_ERROR_WITH_NUMBER,
  PASSWORD_ERROR_AND,
  PASSWORD_ERROR_AND_2,
  PASSWORD_ERROR_USE_LATIN_LETTER,
  PASSWORD_ERROR_LETTERS,
  PASSWORD_ERROR_SIGNS,
  PASSWORD_ERROR_MAY,
} from './constants'
import style from './ChangePasswordValidation.module.css'
import { Languages } from '../../../../interfaces/common'
import { ACCEPTABLE_PASSWORD_SYMBOLS, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '../../../../constants/validator'
import { LanguageContext } from '../../../LanguageContext/LanguageContext'
import { isInvalidPasswordFormat } from '../../../../validator/useFieldValidators'

const SPACE = ' '
const UPPER_CASE_REG_EXP = /[A-ZА-ЯЁЇІЄҐ]/
const LOWER_CASE_REG_EXP = /[a-zа-яёїієґ]/
const CYRILLIC_ALPHABET_REG_EXP = /[а-яА-ЯёЁ]/g
const NUMBER_REG_EXP = /[0-9]/
const ACCEPTABLE_PASSWORD_SYMBOLS_REG_EXP = new RegExp(`[${ACCEPTABLE_PASSWORD_SYMBOLS}]`, 'g')

type ChangePasswordValidationProps = {
  value: string
  isShow?: boolean
}

const checkUnacceptableSymbols = (value: string): boolean => {
  const symbols = value
    .replace(ACCEPTABLE_PASSWORD_SYMBOLS_REG_EXP, '')
    .replace(CYRILLIC_ALPHABET_REG_EXP, '')
    .replace(/\s/g, '')

  return Boolean(symbols)
}

export const ChangePasswordValidation: FC<ChangePasswordValidationProps> = ({ value, isShow = false }) => {
  const t = useTranslations()
  const { language } = useContext(LanguageContext)

  const necessarilyUse = t(PASSWORD_ERROR_NECESSARY_USE)
  const minAndMaxLength = t(PASSWORD_ERROR_MIN_AND_MAX_LENGHT)
  const withoutSpaces = t(PASSWORD_ERROR_WITHOUT_SPACES)
  const withUpperCase = t(PASSWORD_ERROR_WITH_UPPER_CASE)
  const withLowerCase = t(PASSWORD_ERROR_WITH_LOWER_CASE)
  const and = t(PASSWORD_ERROR_AND)
  const and2 = t(PASSWORD_ERROR_AND_2)
  const withNumber = t(PASSWORD_ERROR_WITH_NUMBER)
  const useLatinLetter = t(PASSWORD_ERROR_USE_LATIN_LETTER)
  const letters = t(PASSWORD_ERROR_LETTERS)
  const signs = t(PASSWORD_ERROR_SIGNS)
  const may = t(PASSWORD_ERROR_MAY)

  const isMinAndMaxLength = value.length < PASSWORD_MIN_LENGTH || value.length > PASSWORD_MAX_LENGTH
  const isWithSpace = includes(value, SPACE)
  const isWithUpperCase = UPPER_CASE_REG_EXP.test(value)
  const isWithLowerCase = LOWER_CASE_REG_EXP.test(value)
  const isWithNumberCase = NUMBER_REG_EXP.test(value)
  const isValidPassword = !isInvalidPasswordFormat(value)

  const minLengthClass = classnames({
    [style.error]: isShow && isMinAndMaxLength,
    [style.success]: isShow && !isMinAndMaxLength,
  })

  const withSpaceClass = classnames({
    [style.error]: isShow && !isMinAndMaxLength && isWithSpace,
  })

  const withUpperCaseClass = classnames({
    [style.error]: isShow && !isMinAndMaxLength && !isWithUpperCase,
    [style.success]: isShow && !isMinAndMaxLength && isWithUpperCase,
  })

  const withLowerCaseClass = classnames({
    [style.error]: isShow && !isMinAndMaxLength && !isWithLowerCase,
    [style.success]: isShow && !isMinAndMaxLength && isWithLowerCase,
  })

  const withNumberCaseClass = classnames({
    [style.error]: isShow && !isMinAndMaxLength && !isWithNumberCase,
    [style.success]: isShow && !isMinAndMaxLength && isWithNumberCase,
  })

  const isLatinLetterClass = classnames({
    [style.error]: isShow && !isMinAndMaxLength && CYRILLIC_ALPHABET_REG_EXP.test(value),
  })

  const changePasswordValidationClass = classnames(style.changePasswordValidation, {
    [style.error]: isShow && !value,
    [style.success]: isShow && !isMinAndMaxLength && !isWithSpace && isValidPassword,
  })

  const withSignsClass = classnames({
    [style.error]: isShow && !isMinAndMaxLength && checkUnacceptableSymbols(value),
  })

  checkUnacceptableSymbols(value)

  if (language === Languages.UZ) {
    return (
      <div className={changePasswordValidationClass}>
        {necessarilyUse} <span className={minLengthClass}>{minAndMaxLength}</span>,{' '}
        <span className={withUpperCaseClass}>{withUpperCase}</span>,{' '}
        <span className={withLowerCaseClass}>{withLowerCase}</span> {and}{' '}
        <span className={withNumberCaseClass}>{withNumber}</span>.{' '}
        <span className={isLatinLetterClass}>{useLatinLetter}</span>, {letters},{' '}
        <span className={withSignsClass}>{signs}</span> {may}
      </div>
    )
  }

  return (
    <div className={changePasswordValidationClass}>
      {necessarilyUse} <span className={minLengthClass}>{minAndMaxLength}</span>,{' '}
      <span className={withUpperCaseClass}>{withUpperCase}</span>,{' '}
      <span className={withLowerCaseClass}>{withLowerCase}</span> {and}{' '}
      <span className={withNumberCaseClass}>{withNumber}</span>. {may}{' '}
      <span className={isLatinLetterClass}>{useLatinLetter}</span>, {letters},{' '}
      <span className={withSpaceClass}>{withoutSpaces}</span> {and2} <span className={withSignsClass}>{signs}</span>
    </div>
  )
}
